import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  IndentDecrease,
  IndentIncrease,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/gb/field";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

/** A phase as the step-2 form holds it, before it is saved. */
export type PhaseDraft = {
  /** Present for a phase that already exists on the server. */
  id?: string;
  /** The form's own key for the row, which survives a reorder. */
  key: string;
  /** Depth in the outline: 0 a phase, 1 a sub-phase, and so on. */
  level: number;
  name: string;
  startDate: string;
  endDate: string;
  note: string;
  origin: "manual" | "parsed";
  editedByUser: boolean;
  /** Filename the phase was parsed from, for the "from …" hint. */
  sourceFilename?: string;
  sourceDocumentId?: string;
};

/** How far each level is set in, in pixels. */
const INDENT_PX = 28;

/**
 * One editable phase.
 *
 * Reordering is a pair of up/down buttons rather than a drag handle. The design
 * shows a handle, but a drag-only control is unusable by keyboard and by most
 * assistive tech; buttons are the accessible equivalent and are what a
 * `@dnd-kit` implementation would have to expose alongside the handle anyway.
 *
 * A sub-phase is set in by its level, and indent and outdent move it in the
 * outline (2026-10-03). A phase with sub-phases folds them away, and says
 * when they run: worked out from their dates for display, never saved.
 */
export function PhaseCard({
  phase,
  number,
  error,
  canMoveUp,
  canMoveDown,
  canIndent,
  canOutdent,
  group,
  onChange,
  onMove,
  onShift,
  onRemove,
}: {
  phase: PhaseDraft;
  /** Its number in the outline: `3`, `3.2`. */
  number: string;
  error?: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canIndent: boolean;
  canOutdent: boolean;
  /** Present when it has sub-phases. */
  group?: {
    count: number;
    folded: boolean;
    span: { start: string | null; end: string | null } | null;
    onToggle: () => void;
  };
  onChange: (patch: Partial<PhaseDraft>) => void;
  onMove: (direction: -1 | 1) => void;
  onShift: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  // Any edit to a parsed phase is recorded — it is a parse-quality signal, not
  // a display concern.
  const edit = (patch: Partial<PhaseDraft>): void =>
    onChange({
      ...patch,
      editedByUser: phase.editedByUser || phase.origin === "parsed",
    });

  const control =
    "flex size-8 items-center justify-center rounded-control text-muted hover:bg-subtle hover:text-ink disabled:opacity-30";

  return (
    <li
      className={cn(
        "rounded-card border-[1.5px] border-border bg-surface px-4 py-3.5 shadow-flat",
        phase.level > 0 && "bg-surface-sunken",
      )}
      style={{ marginLeft: phase.level * INDENT_PX }}
    >
      <div className="mb-2.5 flex items-center gap-2">
        {group && (
          <button
            type="button"
            onClick={group.onToggle}
            aria-expanded={!group.folded}
            aria-label={`${group.folded ? "Show" : "Hide"} ${group.count === 1 ? "the sub-phase" : `the ${group.count} sub-phases`} of phase ${number}`}
            className="-ml-1.5 flex size-7 items-center justify-center rounded-control text-muted hover:bg-subtle hover:text-ink"
          >
            {group.folded ? <ChevronRight size={17} /> : <ChevronDown size={17} />}
          </button>
        )}
        <span className="font-display text-[0.72rem] font-semibold uppercase tracking-[0.09em] text-muted">
          Phase {number}
        </span>
        {group && (
          <span className="text-[0.74rem] text-muted">
            {group.count} {group.count === 1 ? "sub-phase" : "sub-phases"}
          </span>
        )}
        {phase.sourceFilename && (
          <span className="truncate text-[0.74rem] text-faint">
            from {phase.sourceFilename}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => onShift(-1)}
            disabled={!canOutdent}
            aria-label={`Move phase ${number} out one level`}
            title="Out one level"
            className={control}
          >
            <IndentDecrease size={16} />
          </button>
          <button
            type="button"
            onClick={() => onShift(1)}
            disabled={!canIndent}
            aria-label={`Make phase ${number} a sub-phase of the one above`}
            title="Make it a sub-phase"
            className={control}
          >
            <IndentIncrease size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={!canMoveUp}
            aria-label={`Move phase ${number} up`}
            className={control}
          >
            <ChevronUp size={17} />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={!canMoveDown}
            aria-label={`Move phase ${number} down`}
            className={control}
          >
            <ChevronDown size={17} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Delete phase ${number}`}
            className="flex size-8 items-center justify-center rounded-control text-muted hover:bg-subtle hover:text-danger"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <Field label="Name" error={error}>
          {(props) => (
            <Input
              {...props}
              value={phase.name}
              placeholder="Civil works & groundworks"
              onChange={(e) => edit({ name: e.target.value })}
            />
          )}
        </Field>
        <Field label="Start">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={phase.startDate}
              onChange={(e) => edit({ startDate: e.target.value })}
            />
          )}
        </Field>
        <Field label="End">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={phase.endDate}
              onChange={(e) => edit({ endDate: e.target.value })}
            />
          )}
        </Field>
      </div>

      {/* One line, not a 2-row textarea: the note is a sentence, and a
          full-width textarea per phase was ~90px each — enough to push even a
          single phase past the fold. */}
      <Field label="Note" optional className="mt-3">
        {(props) => (
          <Input
            {...props}
            value={phase.note}
            placeholder="Access road first — ground conditions wet until March."
            onChange={(e) => edit({ note: e.target.value })}
          />
        )}
      </Field>

      {group?.span && <SpanLine span={group.span} />}
    </li>
  );
}

/**
 * When a phase's sub-phases run, from their own dates. Said beside the phase's
 * own dates rather than written into them: nothing is saved that the
 * programme did not say.
 */
export function SpanLine({
  span,
  className,
}: {
  span: { start: string | null; end: string | null };
  className?: string;
}) {
  const from = span.start ? formatDate(span.start) : "no start date";
  const to = span.end ? formatDate(span.end) : "no end date";
  return (
    <p className={cn("mt-2.5 text-[0.8rem] text-muted", className)}>
      Its sub-phases run {from} – {to}.
    </p>
  );
}
